import { useState, useEffect, useMemo } from "react";
import {
  ActionButton,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Text,
  View,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import Emitter from "../../../emitter";
import {
  API_CALL_ANSWER,
  API_CALL_REJECT,
  CALL_STATES,
} from "../../../constants/constants";
import { RootState } from "../../../store/storeConfig";
import "../style.css";
import { CallRinging, CallReject, CallAccept } from "../../common/Icons";
import { formatLabel, getFormattedPhoneNumber } from "../../../utils/utils";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";
import { getInitialsAndColor } from "../../../utils";
import { SpeedDialData } from "../../../types/generic.types";

const CallAcceptDialog = () => {
  const [isOpen, setOpen] = useState(true);
  const call = useSelector((state: RootState) => state.call);
  const agent = useSelector((state: RootState) => state.agent);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );

  const callCustomerAttributes = call?.customer?.attributes || {};
  const agentBusinessAttributes = agent?.businessAttributes || [];

  useEffect(() => {
    if (call && call?.id && call?.self?.direction !== "OUTBOUND") {
      setOpen(true);
    }
  }, [call?.id]);

  const rejectCallHandler = () => {
    Emitter.emit(API_CALL_REJECT, {
      body: { conversationId: call.self?.id },
    });
    setOpen(false);
  };

  const pickupCallHandler = () => {
    Emitter.emit(API_CALL_ANSWER, {
      body: { conversationId: call.self?.id },
    });
    setOpen(false);
  };

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs: callCustomerAttributes,
    businessAttrs: agentBusinessAttributes,
    type: "call",
  });

  const getCustomerName = () => {
    // Get phone number for speed dial lookup
    const phoneNumber =
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      "";

    const formattedCleanNumber = getFormattedPhoneNumber(phoneNumber);

    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
    }

    // Fall back to customer attributes if speed dial doesn't have names
    if (
      call?.customer?.attributes?.FIRSTNAME &&
      call?.customer?.attributes?.LASTNAME
    ) {
      return `${call?.customer?.attributes?.FIRSTNAME} ${call?.customer?.attributes?.LASTNAME}`;
    }

    return "";
  };

  const getCustomerPhone = () => {
    return (
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      "-"
    );
  };

  const hasCustomerName = () => {
    // Get phone number for speed dial lookup
    const phoneNumber =
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      "";

    const formattedCleanNumber = getFormattedPhoneNumber(phoneNumber);

    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return true;
    }

    // Fall back to customer attributes if speed dial doesn't have names
    return !!(
      call?.customer?.attributes?.FIRSTNAME &&
      call?.customer?.attributes?.LASTNAME
    );
  };

  const { initials, backgroundColor } = useMemo(() => {
    // Get phone number for speed dial lookup
    const phoneNumber =
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      "";

    // Clean phone number for proper matching (remove special characters except +)
    const cleanPhoneNumber = phoneNumber
      ? phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
      : "";

    // Ensure the number starts with + if it has digits
    const formattedCleanNumber =
      cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
        ? `+${cleanPhoneNumber}`
        : cleanPhoneNumber;

    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );

    let firstName = "";
    let lastName = "";

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      firstName = speedDialEntry.firstName;
      lastName = speedDialEntry.lastName;
    } else {
      firstName = call?.customer?.attributes?.FIRSTNAME || "";
      lastName = call?.customer?.attributes?.LASTNAME || "";
    }

    // If both names exist, use them; otherwise fallback to "N A"
    const nameForInitials =
      firstName && lastName ? `${firstName} ${lastName}` : "N A";

    return getInitialsAndColor(nameForInitials) as {
      initials: string;
      backgroundColor: string;
    };
  }, [call?.customer?.phoneNumber, speedDials]);

  const finalAttributes = (
    <View UNSAFE_className="call-incoming-attributes">
      <Flex direction="row" wrap="wrap" gap="size-200">
        {groupedAttributes.flat().map((attribute, index) => (
          <Flex
            key={`attribute-${index}`}
            direction="column"
            UNSAFE_style={{
              flex: "1 1 45%",
              minWidth: "45%",
              maxWidth: "45%",
              marginBottom: "15px",
            }}
          >
            <Text
              UNSAFE_className="call-content-heading"
              UNSAFE_style={{
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {attribute.attributeName.toLowerCase() === "notes"
                ? "Transfer Notes"
                : formatLabel(attribute.attributeName)}
            </Text>
            <Text
              UNSAFE_className="call-content-descritpion"
              UNSAFE_style={{
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {callCustomerAttributes[attribute.attributeName]}
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );

  return (
    <>
      {isOpen &&
        call?.self?.state === CALL_STATES.RINGING &&
        call?.self?.direction !== "OUTBOUND" &&
        !(call?.isSilentMonitor || call?.isBarge) && (
          <DialogTrigger type="modal" mobileType="modal" defaultOpen={true} isKeyboardDismissDisabled>
            <ActionButton isHidden isQuiet></ActionButton>
            {() => (
              <Dialog size={"S"} UNSAFE_className="call-incoming-dialog">
                <Heading level={4}>
                  <Flex
                    direction={"row"}
                    gap={"size-100"}
                    alignItems={"center"}
                  >
                    <Text UNSAFE_className="call-heading-icon">
                      <CallRinging />
                    </Text>
                    <Text UNSAFE_className="call-heading-text">
                      New incoming call
                    </Text>
                  </Flex>
                </Heading>
                <Divider
                  marginBottom={"size-0"}
                  UNSAFE_className="call-incoming-divider"
                />
                <Content maxWidth={"100vw"} marginTop={"15px"}>
                  <Content>
                    <Flex direction={"column"} gap={"size-200"} width={"100%"}>
                      <Flex direction={"row"} width={"100%"}>
                        <Flex direction={"column"} width={"45%"}>
                          <Flex>
                            <View
                              UNSAFE_style={{
                                backgroundColor,
                                borderRadius: "50%",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "12px",
                              }}
                            >
                              <Text UNSAFE_className="chat-header-agent-text">
                                {initials}
                              </Text>
                            </View>
                            <View marginX={12}>
                              <Heading level={3} margin={0}>
                                <Text UNSAFE_className="call-history-detail-title">
                                  {hasCustomerName()
                                    ? getCustomerName()
                                    : getCustomerPhone()}
                                </Text>
                              </Heading>
                              {hasCustomerName() && (
                                <Text UNSAFE_className="call-history-detail-phone-number">
                                  {getCustomerPhone()}
                                </Text>
                              )}
                            </View>
                          </Flex>
                        </Flex>
                      </Flex>
                      {finalAttributes}
                    </Flex>
                  </Content>
                </Content>
                <ButtonGroup>
                  <Button
                    UNSAFE_className="call-reject-btn"
                    variant="negative"
                    onPress={rejectCallHandler}
                    autoFocus={false}
                  >
                    <span className="reject-btn-icon">
                      <CallReject />
                    </span>
                    <Text UNSAFE_style={{ display: "contents" }}>Reject</Text>
                  </Button>
                  <Button
                    variant="accent"
                    UNSAFE_className="call-accept-btn"
                    onPress={pickupCallHandler}
                    autoFocus={false}
                  >
                    <span className="calling-btn-icon">
                      <CallAccept />
                    </span>
                    <Text UNSAFE_style={{ display: "contents" }}>Accept</Text>
                  </Button>
                </ButtonGroup>
              </Dialog>
            )}
          </DialogTrigger>
        )}
    </>
  );
};

export default CallAcceptDialog;
